/**
 * /el/guides/plano-trapezion-gamou
 *
 * The Greek counterpart of `wedding-seating-plan.en.js`. An ADAPTATION, not a
 * translation — the Greek search research is the reason each of the following
 * differs from the English article rather than mirroring it:
 *
 *  - **The slug and the vocabulary.** «Πλάνο τραπεζιών» is the term Greek
 *    couples and Greek wedding editorial actually use. «Σχέδιο θέσεων» and
 *    «διάταξη τραπεζιών» exist as vendor variants and are deliberately not the
 *    headline term. The site's own approved Greek already calls this surface
 *    «Τραπέζια γάμου», which is where the eyebrow comes from.
 *
 *  - **No "seating chart vs seating plan".** The English cluster has a whole
 *    guide resolving that ambiguity. It is an ambiguity between two ENGLISH
 *    phrases and has no Greek equivalent, so this article uses one term
 *    throughout and never implies there are two things.
 *
 *  - **Assigned seating is assumed, not argued for.** The English §1 asks
 *    whether you need a seating plan at all, which is a real question at an
 *    Anglophone wedding. At a Greek δεξίωση assigned tables are the norm, so
 *    the equivalent section asks the question Greek couples do have: whether
 *    you assign the TABLE or the SEAT.
 *
 *  - **Greek reception furniture.** Ροτόντες, το νυφικό τραπέζι, τραπέζι σε
 *    σχήμα Π. No inches, and no US table sizes — the English cluster's
 *    60"/72" framing does not transfer and is not repeated here.
 *
 *  - **Κουμπάροι** rather than "the wedding party", which is not a Greek
 *    category.
 *
 * ── The claims this guide is allowed to make ──────────────────────────────
 * Identical to the English article's, and bounded by the same seating
 * capability audit that governs /wedding-seating. Restated because a
 * translation is exactly where a bounded claim quietly grows:
 *
 *  - The seating pool is APPROVED RECEPTION ATTENDEES, never the RSVP
 *    headcount and never the guest list.
 *  - A cancellation does NOT empty a seat. The affected seating is surfaced
 *    for review; the couple decides. Never «ενημερώνεται αυτόματα» or
 *    «ελευθερώνει τη θέση».
 *  - Tables are created by the couple, capacity is the one hard limit, and
 *    preferences are advisory.
 *  - Exports are Excel and a print-ready A4 landscape PDF. Nothing else.
 *
 * And the four things this guide must never say, because none of them exist:
 * drag-and-drop, a visual floor plan or canvas, automatic table generation,
 * and any automatic seating algorithm or reassignment. This matters more in
 * Greek than in English: the free Greek tools this article competes with DO
 * offer drag-and-drop, so the temptation to match them is real and the answer
 * is no.
 *
 * Plan availability: the closing section carries a note block stating that
 * seating is Heirloom-only. Keep it.
 */

const weddingSeatingPlanEl = {
  slug: "plano-trapezion-gamou",

  title: "Πλάνο τραπεζιών γάμου: πώς να το φτιάξετε βήμα βήμα",
  metaTitle: "Πλάνο τραπεζιών γάμου: πώς να το φτιάξετε βήμα βήμα | Agapaé",
  metaDescription:
    "Πώς φτιάχνεται το πλάνο τραπεζιών του γάμου: από τις απαντήσεις των καλεσμένων στις παρέες, στη χωρητικότητα των τραπεζιών και στις αλλαγές της τελευταίας στιγμής.",

  eyebrow: "Τραπέζια γάμου",

  hero: {
    src: "/assets/images/moments/moment-2.jpg",
    // Describes the photograph, not the keyword — same rule as the English
    // module, and the same photograph.
    alt: "Ασπρόμαυρη κοντινή λήψη νύφης που κρατά μπουκέτο με μικρά λευκά λουλούδια, με το κεντημένο μανίκι της σε απαλή εστίαση",
    width: 6983,
    height: 3436,
  },

  intro: [
    `Η λίστα καλεσμένων σάς λέει ποιοι έρχονται. Το πλάνο τραπεζιών αποφασίζει πού θα καθίσουν.`,
    `Είναι το κομμάτι της οργάνωσης που μοιάζει απλό μέχρι να το πιάσετε στα χέρια σας.`,
    `Έχετε ζευγάρια, οικογένειες, παρέες που γνωρίζονται χρόνια, ανθρώπους που θα συστηθούν εκείνο το βράδυ, συνοδούς, προτιμήσεις — και πού και πού μια σχέση που θέλει λίγη περισσότερη σκέψη από ένα όνομα σε μια λίστα.`,
    `Ο πιο εύκολος τρόπος να γίνει διαχειρίσιμο είναι να μην ξεκινήσετε από τα τραπέζια.`,
    `Ξεκινήστε από τους ανθρώπους.`,
    `Όταν ξέρετε ακριβώς ποιοι θα είναι στη δεξίωση, το πλάνο χτίζεται γύρω από τις σχέσεις, τους πραγματικούς περιορισμούς του χώρου και την ατμόσφαιρα που θέλετε.`,
    `Δείτε πώς περνάτε από τις επιβεβαιωμένες απαντήσεις σε ένα πλάνο που θα σας χρησιμεύσει πραγματικά την ημέρα του γάμου.`,
  ],

  sections: [
    {
      heading: "1. Τραπέζι ή θέση; Αποφασίστε πρώτα πόση λεπτομέρεια χρειάζεστε",
      content: [
        `Στους περισσότερους γάμους στην Ελλάδα οι καλεσμένοι ξέρουν σε ποιο τραπέζι κάθονται. Το ερώτημα σπάνια είναι αν θα υπάρχει πλάνο· είναι πόσο αναλυτικό θα είναι.`,
        `Έχετε τρεις επιλογές:`,
        {
          type: "list",
          items: [
            `ελεύθερη επιλογή θέσης, χωρίς κατανομή`,
            `κατανομή σε τραπέζι, με τους καλεσμένους να διαλέγουν θέση μέσα σε αυτό`,
            `κατανομή σε συγκεκριμένη θέση σε συγκεκριμένο τραπέζι`,
          ],
        },
        `Η μεσαία επιλογή καλύπτει τις περισσότερες δεξιώσεις. Είναι αρκετή για να ξέρει ο καθένας πού ανήκει και αρκετά χαλαρή ώστε να μη χρειάζεται καρτελάκι σε κάθε πιάτο.`,
        `Η απόφαση συνήθως εξαρτάται από:`,
        {
          type: "list",
          items: [
            `το μέγεθος και τη διάταξη του χώρου`,
            `τον αριθμό των καλεσμένων`,
            `το αν τα τραπέζια έχουν σταθερή χωρητικότητα`,
            `το αν ο χώρος έχει δικές του απαιτήσεις για τη διάταξη`,
            `το πόσο επίσημη ή χαλαρή θέλετε να είναι η βραδιά`,
          ],
        },
        `Στόχος δεν είναι το πιο αναλυτικό πλάνο.`,
        `Στόχος είναι να έχει ο καθένας μια θέση όπου θα περάσει καλά.`,
      ],
    },
    {
      heading: "2. Ξεκινήστε από τις απαντήσεις των καλεσμένων",
      content: [
        `Μόλις αποφασίσετε ότι θα υπάρχει κατανομή, μην ανοίξετε ένα λευκό χαρτί και αρχίσετε να γράφετε ονόματα.`,
        `Ξεκινήστε από τις απαντήσεις.`,
        `Η πρώτη ερώτηση είναι απλή:`,
        `Ποιοι έρχονται τελικά στη δεξίωση;`,
        `Η λίστα σας πιθανότατα περιέχει ανθρώπους που:`,
        {
          type: "list",
          items: [
            `έχουν επιβεβαιώσει ότι θα έρθουν`,
            `έχουν απαντήσει αρνητικά`,
            `δεν έχουν απαντήσει ακόμη`,
            `έχουν ζητήσει ή δηλώσει συνοδό`,
            `θα έρθουν στο μυστήριο αλλά όχι στη δεξίωση`,
          ],
        },
        `Μόνο κάποιοι από αυτούς ανήκουν στο πλάνο τραπεζιών.`,
        `Γι' αυτό είναι πολύ πιο εύκολο να φτιάξετε το πλάνο πάνω σε μια ενημερωμένη εικόνα των απαντήσεων παρά να συντηρείτε μια ξεχωριστή λίστα στο χέρι.`,
        {
          type: "p",
          text: [
            `Αν οι απαντήσεις των καλεσμένων δεν έχουν οργανωθεί ακόμη, ξεκινήστε από τη `,
            { text: "συγκέντρωση των RSVP", href: "/wedding-rsvp" },
            ` και μετά χτίστε το πλάνο.`,
          ],
        },
      ],
    },
    {
      heading: "3. Το μυστήριο και η δεξίωση δεν έχουν πάντα τους ίδιους ανθρώπους",
      content: [
        `Ένας γάμος έχει περισσότερες από μία εκδηλώσεις, και δεν έρχονται όλοι σε όλες.`,
        `Κάποιοι θα είναι στην εκκλησία και όχι στη δεξίωση. Άλλοι θα έρθουν και στα δύο. Σε γάμους με πολλούς καλεσμένους από άλλη πόλη, η διαφορά μπορεί να είναι δεκάδες άτομα.`,
        `Για το πλάνο τραπεζιών μετράει ένα μόνο πράγμα: ποιοι θα καθίσουν στη δεξίωση.`,
        `Πριν λοιπόν βάλετε οποιονδήποτε σε τραπέζι, βεβαιωθείτε ότι η λίστα σας δείχνει παρουσία στη δεξίωση και όχι απλώς τον συνολικό αριθμό των καλεσμένων του γάμου.`,
        `Αυτή η μικρή διάκριση προλαμβάνει ένα πολύ συνηθισμένο πρόβλημα: πλάνο χτισμένο γύρω από ανθρώπους που δεν χρειάζονται θέση.`,
        `Σας δίνει επίσης πολύ πιο αξιόπιστη αφετηρία για να υπολογίσετε πόσα τραπέζια χρειάζεστε.`,
      ],
    },
    {
      heading: "4. Φτιάξτε πρώτα τα τραπέζια, μετά βάλτε τους ανθρώπους",
      content: [
        `Τώρα κοιτάξτε τον χώρο.`,
        `Ξεκινήστε από τα πρακτικά:`,
        {
          type: "list",
          items: [
            `Πόσα τραπέζια υπάρχουν διαθέσιμα;`,
            `Πόσοι κάθονται άνετα στο καθένα;`,
            `Είναι ροτόντες, μακρόστενα ή συνδυασμός;`,
            `Υπάρχουν τραπέζια με σταθερή θέση στην αίθουσα;`,
            `Υπάρχουν σημεία που επηρεάζουν το πού είναι λογικό να καθίσει κάποιος — η πίστα, η μουσική, η είσοδος, ο μπουφές;`,
          ],
        },
        `Σε αυτό το στάδιο δεν χρειάζεται να αποφασίσετε ποιος κάθεται πού.`,
        `Φτιάχνετε απλώς το πλαίσιο μέσα στο οποίο θα μπουν οι καλεσμένοι.`,
        `Έχει σημασία, γιατί η κατανομή είναι πολύ πιο εύκολη όταν ξέρετε πρώτα τα όριά της.`,
        `Αντί να ρωτάτε:`,
        `«Πού θα βάλω αυτούς τους οκτώ;»`,
        `μπορείτε να ρωτήσετε:`,
        `«Ποιο από τα τραπέζια μου είναι το σωστό για αυτούς τους οκτώ;»`,
        {
          type: "note",
          text: `Ζητήστε από τον χώρο τη διάταξη της αίθουσας και τη χωρητικότητα ανά τραπέζι πριν αρχίσετε. Είναι δύο πληροφορίες που αλλάζουν όλο το πλάνο και δίνονται σε ένα τηλεφώνημα.`,
        },
      ],
    },
    {
      heading: "5. Δείτε τις παρέες, όχι μόνο τα ονόματα",
      content: [
        `Η λίστα σας δεν είναι ονόματα· είναι σχέσεις.`,
        `Πριν μοιράσετε τραπέζια, δείτε ποιες ομάδες υπάρχουν ήδη μέσα της:`,
        {
          type: "list",
          items: [
            `στενή οικογένεια`,
            `συγγενείς`,
            `οι κουμπάροι`,
            `παιδικοί φίλοι`,
            `φίλοι από τη σχολή`,
            `συνάδελφοι`,
            `ζευγάρια`,
            `συγγενείς που ήδη γνωρίζονται μεταξύ τους`,
            `καλεσμένοι που έρχονται μόνοι τους`,
          ],
        },
        `Αυτές οι ομάδες είναι καλή αφετηρία, γιατί οι άνθρωποι νιώθουν πιο άνετα όταν έστω κάποιοι γύρω τους είναι γνώριμοι.`,
        `Μην τις πάρετε όμως ως κανόνα.`,
        `Ένα τραπέζι φτιαγμένο εξ ολοκλήρου από μία παρέα είναι κάποιες φορές λιγότερο ευχάριστο από ένα προσεκτικά ανάμεικτο. Και ένας φίλος που ξέρει κόσμο από δύο διαφορετικούς κύκλους μπορεί να είναι ακριβώς η γέφυρα ανάμεσά τους.`,
        `Σκεφτείτε τις παρέες ως υλικό, όχι ως κουτιά μέσα στα οποία πρέπει να μείνουν όλοι.`,
      ],
    },
    {
      heading: "6. Ποιοι ταιριάζουν μαζί — και ποιοι όχι",
      content: [
        `Εδώ είναι συνήθως το πιο ανθρώπινο κομμάτι της δουλειάς.`,
        `Ξεκινήστε από τα εύκολα.`,
        `Ποιοι θα περνούσαν φυσιολογικά ένα ωραίο βράδυ μαζί;`,
        `Μετά περάστε στα πιο δύσκολα.`,
        `Υπάρχουν καλεσμένοι που:`,
        {
          type: "list",
          items: [
            `σας έχουν ζητήσει ρητά να καθίσουν μαζί;`,
            `θα ένιωθαν καλύτερα αν ήξεραν κάποιον στο τραπέζι τους;`,
            `κινούνται σε πολύ διαφορετικούς κύκλους;`,
            `έχουν δύσκολο ιστορικό μεταξύ τους;`,
            `θα ένιωθαν άβολα δίπλα σε συγκεκριμένο άτομο;`,
            `θα χαρούν να γνωρίσουν καινούριους ανθρώπους;`,
          ],
        },
        `Η προτίμηση ενός καλεσμένου είναι χρήσιμη πληροφορία, αλλά δεν έχει πάντα τέλεια λύση.`,
        `Κάποιος μπορεί να θέλει να καθίσει με έναν άνθρωπο που έχει ήδη ζητήσει αλλού. Ή μια παρέα να είναι πολύ μεγάλη για να χωρέσει άνετα σε ένα τραπέζι.`,
        `Οι προτιμήσεις είναι στοιχεία που μπαίνουν στην απόφαση, όχι εντολές που πρέπει οπωσδήποτε να τηρηθούν.`,
        `Όταν δεν μπορείτε να τις ικανοποιήσετε όλες, προτεραιότητα έχει η συνολική άνεση και το πώς λειτουργεί το τραπέζι ως σύνολο.`,
      ],
    },
    {
      heading: "7. Πόσοι καλεσμένοι σε κάθε τραπέζι",
      content: [
        `Δεν υπάρχει ένας αριθμός που δουλεύει παντού.`,
        `Οι ροτόντες στις ελληνικές δεξιώσεις φιλοξενούν συνήθως γύρω στα οκτώ με δέκα άτομα, ενώ τα μακρόστενα τραπέζια παίρνουν περισσότερα και δίνουν πιο επίσημη εικόνα. Το νυφικό τραπέζι ακολουθεί τους δικούς του κανόνες, ανάλογα με το πόσους θέλετε δίπλα σας.`,
        `Ένα τραπέζι που τεχνικά χωράει δέκα δεν είναι απαραίτητα το σωστό τραπέζι για δέκα, αν ο χώρος γύρω του είναι στενός ή αν η διάταξη δυσκολεύει τη συζήτηση.`,
        `Λάβετε υπόψη:`,
        {
          type: "list",
          items: [
            `το σχήμα του τραπεζιού`,
            `τις διαστάσεις του`,
            `τον χώρο που υπάρχει γύρω του`,
            `τις καρέκλες και το πέρασμα πίσω τους`,
            `τι απαιτεί το σερβίρισμα`,
            `τη διακόσμηση που θα πιάσει χώρο στο τραπέζι`,
          ],
        },
        `Το σημαντικό είναι να ορίσετε τα πρακτικά σας όρια πριν αρχίσετε να γεμίζετε τα τραπέζια. Ο χώρος της δεξίωσης είναι η καλύτερη πηγή γι' αυτά.`,
      ],
    },
    {
      heading: "8. Μοιράστε τους καλεσμένους στα τραπέζια",
      content: [
        `Τώρα μπορείτε να αρχίσετε την κατανομή.`,
        {
          type: "subheading",
          text: "Πρώτα όσοι έχουν τις λιγότερες εναλλακτικές",
        },
        `Κάποιοι καλεσμένοι μετακινούνται εύκολα από τραπέζι σε τραπέζι. Άλλοι είναι δεμένοι με μια συγκεκριμένη παρέα ή έχουν ιδιαιτερότητες.`,
        `Τοποθετήστε πρώτα τις λιγότερο ευέλικτες ομάδες.`,
        {
          type: "subheading",
          text: "Κρατήστε μαζί όσους έρχονται μαζί",
        },
        `Αν κάποιοι έρχονται ως ζευγάρι ή ως παρέα, μην τους χωρίσετε κατά λάθος επειδή δύο τραπέζια είχαν διαφορετικό υπόλοιπο.`,
        {
          type: "subheading",
          text: "Συμπληρώστε τα υπόλοιπα με σκέψη",
        },
        `Μόλις τοποθετηθούν οι δύσκολες ομάδες, χρησιμοποιήστε τις κενές θέσεις για να φέρετε κοντά ανθρώπους που ταιριάζουν και να ισορροπήσετε τα τραπέζια.`,
        {
          type: "subheading",
          text: "Κοιτάξτε το τραπέζι ως σύνολο",
        },
        `Μην κρίνετε ένα τραπέζι μόνο από το αν γέμισε.`,
        `Ρωτήστε:`,
        `«Θα περνούσαν όντως καλά αυτοί οι άνθρωποι μαζί;»`,
        `Ένα τραπέζι με μία κενή θέση είναι κάποιες φορές καλύτερο από ένα γεμάτο τραπέζι που δεν δένει.`,
      ],
    },
    {
      heading: "9. Ελέγξτε το πλάνο πριν το κλείσετε",
      content: [
        `Το πλάνο δεν είναι έτοιμο μόλις μπει ο τελευταίος καλεσμένος σε τραπέζι.`,
        `Πριν το θεωρήσετε τελικό, δείτε το συνολικά.`,
        `Ελέγξτε για:`,
        {
          type: "list",
          items: [
            `τραπέζια που ξεπερνούν τη χωρητικότητά τους`,
            `καλεσμένους που έμειναν χωρίς τραπέζι`,
            `ζευγάρια ή παρέες που χωρίστηκαν κατά λάθος`,
            `προτιμήσεις που έμειναν ανοιχτές`,
            `σχέσεις που θέλουν άλλη μια ματιά`,
            `καλεσμένους που μπορεί να νιώσουν μόνοι στο τραπέζι τους`,
            `απαντήσεις που άλλαξαν από τότε που ξεκινήσατε`,
            `συνοδούς που τελικά δεν έρχονται`,
          ],
        },
        `Αυτός ο έλεγχος είναι συνήθως εκεί που το πλάνο βελτιώνεται περισσότερο.`,
        `Πολλές φορές η μετακίνηση ενός ανθρώπου λύνει τρία προβλήματα ταυτόχρονα.`,
        `Προσοχή όμως: δεν είναι κάθε εύρημα λάθος του πλάνου. Κάποια είναι απλώς αποφάσεις που αξίζουν άλλη μια σκέψη.`,
      ],
    },
    {
      heading: "10. Ξαναδείτε το πλάνο σε κάθε αλλαγή",
      content: [
        `Το πλάνο σπάνια τελειώνει την πρώτη φορά που θα το φτιάξετε.`,
        `Κάποιος θα ακυρώσει. Ένας συνοδός θα αλλάξει. Κάποιος που δεν είχε απαντήσει θα επιβεβαιώσει δέκα μέρες πριν.`,
        `Όταν συμβεί, μην υποθέσετε ότι πρέπει να ξαναχτίσετε τα πάντα.`,
        `Ξεκινήστε από το τραπέζι που επηρεάστηκε.`,
        `Και μετά ελέγξτε:`,
        {
          // The numbering lives in the <ol>, so the source strings carry none.
          type: "steps",
          items: [
            `Στέκει ακόμη η χωρητικότητα του τραπεζιού;`,
            `Χωρίστηκε κάποια παρέα χωρίς να το θέλετε;`,
            `Επηρεάζει η αλλαγή τη θέση κάποιου άλλου;`,
            `Έγιναν κάποιες προτιμήσεις πιο δύσκολες;`,
            `Βγάζει ακόμη νόημα το πλάνο συνολικά;`,
          ],
        },
        `Το ζητούμενο είναι να δείτε τις συνέπειες της αλλαγής, αντί να θεωρήσετε δεδομένο ότι όλα τα υπόλοιπα παραμένουν σωστά.`,
        `Καλό πλάνο δεν είναι αυτό που δεν αλλάζει ποτέ.`,
        `Είναι αυτό που ελέγχεται εύκολα όταν αλλάξει.`,
      ],
    },
    {
      heading: "11. Ετοιμάστε την τελική εικόνα για την ημέρα του γάμου",
      content: [
        `Μόλις ελέγξετε το πλάνο, μετατρέψτε το σε κάτι που μπορείτε να χρησιμοποιήσετε την ίδια την ημέρα.`,
        `Ανάλογα με το πώς το έχετε στήσει, αυτό μπορεί να περιλαμβάνει:`,
        {
          type: "list",
          items: [
            `την τελική κατανομή ανά τραπέζι`,
            `αριθμούς ή ονόματα τραπεζιών`,
            `μια λίστα όπου βρίσκει κανείς γρήγορα ένα όνομα`,
            `μια έκδοση για τον χώρο ή για όποιον συντονίζει τη βραδιά`,
            `ένα τυπωμένο αντίγραφο για τη δεξίωση`,
            `ένα ψηφιακό αντίγραφο για εσάς`,
          ],
        },
        `Κρατήστε την τελική μορφή απλή.`,
        `Την ημέρα του γάμου κανείς δεν θέλει να αποκρυπτογραφήσει ένα περίπλοκο αρχείο για να βρει το τραπέζι του.`,
        `Το τελικό πλάνο πρέπει να κάνει ένα πράγμα αμέσως σαφές:`,
        `ποιος κάθεται πού.`,
      ],
    },
    {
      heading: "Από τις απαντήσεις στα τραπέζια, χωρίς δεύτερη λίστα",
      content: [
        `Τα περισσότερα εργαλεία για τα τραπέζια ξεκινούν από ένα άδειο σχέδιο.`,
        `Το Agapaé ξεκινά από αλλού: από τους καλεσμένους που βρίσκονται ήδη στη ροή των απαντήσεων και της λίστας σας.`,
        `Μόλις επιβεβαιωθούν και εγκριθούν όσοι έρχονται στη δεξίωση, το Agapaé σάς δίνει ακριβώς αυτούς τους ανθρώπους για το πλάνο, μαζί με το ποιοι έρχονται μεταξύ τους. Δημιουργείτε τα τραπέζια, τοποθετείτε καλεσμένους μεμονωμένα ή ως παρέα, δουλεύετε μέσα στη χωρητικότητα κάθε τραπεζιού, βλέπετε τα σημεία που χρειάζονται προσοχή και κάνετε αλλαγές όσο εξελίσσονται τα πράγματα.`,
        `Αν κάτι αλλάξει αργότερα, το Agapaé δεν αναδιατάσσει μόνο του το πλάνο. Σας δείχνει τι επηρεάστηκε, για να αποφασίσετε εσείς.`,
        `Όταν το πλάνο είναι έτοιμο, εξάγεται σε Excel ή σε έτοιμο για εκτύπωση PDF σε A4 οριζόντιο.`,
        {
          type: "note",
          text: `Τα εργαλεία για τα τραπέζια είναι διαθέσιμα στο πακέτο Heirloom. Ο οδηγός πιο πάνω εξηγεί τη διαδικασία· η σελίδα του προϊόντος δείχνει πώς δουλεύει μέσα στο Agapaé.`,
        },
        `Η ιδέα είναι απλή:`,
        `απαντήσεις → εγκεκριμένοι καλεσμένοι δεξίωσης → τραπέζια → κατανομή → έλεγχος → τελικό πλάνο.`,
        `Χωρίς δεύτερη λίστα που πρέπει να τη χτίσετε από την αρχή.`,
        {
          type: "p",
          text: [
            {
              text: "Δείτε τα τραπέζια γάμου στο Agapaé",
              href: "/wedding-seating",
            },
          ],
        },
      ],
    },
  ],

  faqHeading: "Συχνές ερωτήσεις",

  // Visible copy, always expanded. `faqSchema` is deliberately not set — see
  // the note on `buildArticleJsonLd`.
  faq: [
    {
      q: `Πότε πρέπει να φτιάξω το πλάνο τραπεζιών;`,
      a: `Συνήθως όταν έχετε αξιόπιστη εικόνα του ποιοι έρχονται στη δεξίωση. Μπορείτε να κάνετε μια πρώτη δοκιμή νωρίτερα, αλλά υπολογίστε ότι θα το ξαναδείτε καθώς αλλάζουν οι απαντήσεις.`,
    },
    {
      q: `Να περιμένω να απαντήσουν όλοι οι καλεσμένοι;`,
      a: `Όχι απαραίτητα. Μπορείτε να ομαδοποιήσετε παρέες και να σχεδιάσετε τα τραπέζια από νωρίς, αλλά το τελικό πλάνο πρέπει να στηρίζεται σε αυτούς που όντως έρχονται στη δεξίωση.`,
    },
    {
      q: `Πόσα άτομα κάθονται σε μια ροτόντα;`,
      a: `Συνήθως οκτώ με δέκα, αλλά εξαρτάται από τις διαστάσεις του τραπεζιού, τον χώρο γύρω του και τη διακόσμηση. Ρωτήστε τον χώρο της δεξίωσης — έχει την ακριβή απάντηση για τα δικά του τραπέζια.`,
    },
    {
      q: `Πρέπει τα ζευγάρια να κάθονται μαζί;`,
      a: `Στις περισσότερες περιπτώσεις τα ζευγάρια περιμένουν να καθίσουν μαζί, εκτός αν υπάρχει συγκεκριμένος λόγος για κάτι άλλο. Δείτε κάθε ζευγάρι ή παρέα ξεχωριστά, αντί να εφαρμόσετε έναν κανόνα σε όλους.`,
    },
    {
      q: `Τι κάνω αν κάποιος ακυρώσει αφού έχω φτιάξει το πλάνο;`,
      a: `Δείτε πρώτα το τραπέζι που επηρεάστηκε και ελέγξτε αν η αλλαγή αγγίζει άλλους καλεσμένους, κάποια παρέα ή τη χωρητικότητα. Συνήθως χρειάζεται μια μικρή διόρθωση και όχι να ξαναχτίσετε τα πάντα.`,
    },
  ],

  related: {
    heading: "Πού ταιριάζει αυτό στο Agapaé",

    // Only guides that genuinely have a Greek version may be listed here. The
    // test refuses a slug that is not in the registry, and a Greek guide
    // linking to an English one across languages is worse than not linking.
    // The rest of the Greek seating cluster: the capacity question that comes
    // before this article, and the terminology one that comes before both.
    guides: [
      "how-many-people-wedding-table",
      "wedding-seating-chart-vs-seating-plan",
    ],

    product: [
      {
        href: "/wedding-seating",
        label: "Τραπέζια γάμου",
        blurb:
          "Οργανώστε τα τραπέζια του γάμου σας από τους καλεσμένους που βρίσκονται ήδη στη ροή των απαντήσεων, δείτε τι χρειάζεται προσοχή και ετοιμάστε το τελικό πλάνο στο Agapaé.",
      },
    ],
  },
};

export default weddingSeatingPlanEl;
